import {
  createBrowserRouter, createRoutesFromElements, RouterProvider, Route, Navigate, Outlet,
} from 'react-router-dom'
import { ThemeProvider, CssBaseline } from '@mui/material'
import theme from './theme/theme'
import { AuthProvider, useAuth } from './hooks/useAuth'
import { PlantProvider } from './hooks/usePlantContext'
import AppShell from './components/layout/AppShell'
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import PlanList from './pages/PlanList'
import PlanningBoard from './pages/PlanningBoard'
import PlanComparison from './pages/PlanComparison'
import DataHub from './pages/DataHub'
import Reports from './pages/Reports'
import Rules from './pages/Rules'
import Admin from './pages/Admin'
import Scenarios from './pages/Scenarios'
import CapacityCalendar from './pages/CapacityCalendar'
import WorkingDays from './pages/WorkingDays'
import ChangeoverTimes from './pages/supporting/ChangeoverTimes'
import Capacity from './pages/supporting/Capacity'
import ItemRunRates from './pages/supporting/ItemRunRates'
import CapacityRunRates from './pages/supporting/CapacityRunRates'
import ItemData from './pages/supporting/ItemData'
import Demand from './pages/supporting/Demand'
import CurrentInventory from './pages/supporting/CurrentInventory'
import ECConversion from './pages/supporting/ECConversion'
import PalletCount from './pages/supporting/PalletCount'
import BusinessCalendar from './pages/supporting/BusinessCalendar'

function ProtectedRoute({ children }) {
  const { authenticated } = useAuth()
  if (!authenticated) return <Navigate to="/login" replace />
  return children
}

// A data router (FAR-56): the Planning Board uses useBlocker to warn before
// leaving with unsaved modifications, which only works under one. The routes
// are exactly the ones the old <BrowserRouter><Routes> tree had.
const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path="/login" element={<Login />} />
      <Route
        element={
          <ProtectedRoute>
            <PlantProvider>
              <AppShell />
            </PlantProvider>
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="plans" element={<PlanList />} />
        <Route path="plans/:planId/grid" element={<PlanningBoard />} />
        <Route path="reports" element={<Reports />} />
        <Route path="reports/comparison" element={<PlanComparison />} />
        <Route path="data" element={<DataHub />} />
        <Route path="rules" element={<Rules />} />
        <Route path="scenarios" element={<Scenarios />} />
        {/* FAR-82: the nav's Calendar is the working-days screen. */}
        <Route path="working-days" element={<WorkingDays />} />
        <Route path="capacity-calendar" element={<CapacityCalendar />} />
        {/* Admin performs its own role check internally and renders an
            access-denied state for non-Admin users. */}
        <Route path="admin" element={<Admin />} />
        <Route path="supporting" element={<Outlet />}>
          <Route path="changeover" element={<ChangeoverTimes />} />
          <Route path="capacity" element={<Capacity />} />
          <Route path="item-run-rates" element={<ItemRunRates />} />
          <Route path="capacity-run-rates" element={<CapacityRunRates />} />
          <Route path="item-data" element={<ItemData />} />
          <Route path="demand" element={<Demand />} />
          <Route path="inventory" element={<CurrentInventory />} />
          <Route path="ec-table" element={<ECConversion />} />
          <Route path="pallet-count" element={<PalletCount />} />
          <Route path="calendar" element={<BusinessCalendar />} />
        </Route>
      </Route>
    </>,
  ),
)

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* AuthProvider uses no router hooks, so it can sit outside the router. */}
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
    </ThemeProvider>
  )
}
